import '../../modules/mode-components';

import * as angular from 'angular';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { ChartModelActionType } from '../../actions/chart.actions';
import { ReportLayoutActions } from '../../actions/report-layout.actions';
import { MixableBaseController } from '../../common/component';
import { VizTypes } from '../../common/types';
import { ReportRunResource } from '@mode/legacy/data-access-model-js';
import { QueryRunState, TableState } from '@mode/legacy/types-app-state';
import { ReportDataService } from '../../services/report-data-service';
import { ReportRunDataService } from '../../services/report-run-service';
import ViewService from '../../services/view-service';

class TableDesignerController extends MixableBaseController {
  static $inject = ['RxStoreService', 'ReportDataService', 'ReportRunDataService', 'ViewService'];

  private selectedTable$: Observable<TableState>;
  private tableView$: Observable<any>;
  private tableToken$: Observable<string>;
  private tableToken: string;
  private queryToken: string;
  private queryRun: QueryRunState;
  private dataset: {};
  private reportRun: ReportRunResource;

  constructor(
    private rxStore: AppStore,
    private reportDataService: ReportDataService,
    private reportRunDataService: ReportRunDataService,
    private viewService: ViewService
  ) {
    super();

    this.selectedTable$ = this.reportDataService.selectedTable$.pipe(filter((t) => t != null));

    this.tableView$ = this.selectedTable$.pipe(map((t) => t.currentView));
    this.tableToken$ = this.selectedTable$.pipe(map((t) => t.token));

    this.reportRunDataService.selectedTableState$.pipe(takeUntil(this.destroyed$)).subscribe((data) => {
      this.queryToken = data.queryToken;
      this.queryRun = data.queryRun;
      this.dataset = data.modelState;
      this.reportRun = data.reportRun && data.reportRun.resource;
    });
  }

  $onInit() {
    this.tableToken$.subscribe((token) => (this.tableToken = token));
  }

  selectColumn(id, name) {
    this.viewService.selectTableColumn(id, name);
  }

  /**
   * Remove the given display table from the builder.
   */
  removeTable(tableToken: string) {
    this.rxStore.dispatch(new ReportLayoutActions.RemoveViz(tableToken, VizTypes.Table));
  }

  /**
   * Delete the given display table.
   */
  deleteTable(tableToken: string) {
    this.rxStore.dispatch({
      type: ChartModelActionType.DestroyTable,
      token: tableToken,
      queryToken: this.queryToken,
    });
  }

  /**
   * Determine if the given display table is in the builder.
   */
  hasTable() {
    return this.tableToken && this.viewService.hasTableInLayout(this.tableToken);
  }
}

const tableDesignerComponent: angular.IComponentOptions = {
  bindings: {},
  template: `
<div class="top-header">
  <span class="header-text">Display Table</span>
  <capra-tooltip
    tooltip-content="This chart type is deprecated. <br>Improve performance by removing <br>it from the Report Builder"
    tooltip-placement="bottom"
    tooltip-content-is-html="true">
    <text-badge type="'warning'">Deprecated</text-badge>
  </capra-tooltip>

  <div class="extra-display-table-actions">
    <span ng-if="!ctrl.hasTable()" class="table-removed"><i class="icon-check-mark"></i>Removed from report builder</span>
    <span ng-if="ctrl.hasTable()" class="action-container">
      <button class="btn btn-default remove-display-table" ng-click="ctrl.removeTable(ctrl.tableToken$ | async:this)">Remove from report builder</button>
    </span>
    <button class="btn btn-default delete-display-table" ng-click="ctrl.deleteTable(ctrl.tableToken$ | async:this)"><i class="icon-trash-gray"></i>Delete</button>
  </div>
</div>
<div class="table-designer mode-grid">
  <div class="row">
    <div class="col-md-10 col-md-offset-1">
      <div
        mode-table
        ng-if="ctrl.dataset && ctrl.dataset.state !== 'failed' && ctrl.dataset.state !== 'cancelled' && ctrl.dataset.content && (ctrl.selectedTable$ | async:this)"
        id="{{ ctrl.tableToken$ | async:this }}"
        dataset="ctrl.dataset"
        set-view="ctrl.tableView$ | async:this"
        on-select-column="ctrl.selectColumn(id, name)"
        capacity-override="true">
      </div>
    </div>
  </div>
  <div ng-if="ctrl.dataset && ctrl.dataset.state !== 'failed' && ctrl.dataset.state !== 'cancelled' && !ctrl.dataset.content && (ctrl.selectedTable$ | async:this)" class="editor-error alert alert-warning clearfix">
    <h3 class="pull-left running-text">This dataset is too large to view in browser</h3>
  </div>
  <div ng-if="ctrl.dataset && ctrl.dataset.state === 'failed' && (ctrl.selectedTable$ | async:this)" class="editor-error alert alert-error clearfix">
    <h3 class="pull-left running-text">Query Failed</h3>
  </div>
  <div ng-if="ctrl.reportRun.state !== 'disconnected' && (ctrl.queryRun.running || (!ctrl.dataset && ctrl.queryRun.state === 'succeeded'))" class="empty-report-msg design-loader clearfix">
    <mode-loader></mode-loader>
  </div>
  <div ng-if="ctrl.dataset && ctrl.dataset.state === 'cancelled' && (ctrl.selectedTable$ | async:this)" class="editor-error alert alert-warning clearfix">
    <h3 class="pull-left running-text">This run has been cancelled</h3>
  </div>
</div>
`,
  controller: TableDesignerController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('tableDesigner', tableDesignerComponent);
